/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';

import { callback } from '../fn/operations/callback';
import { Callback$Params } from '../fn/operations/callback';
import { login } from '../fn/operations/login';
import { Login$Params } from '../fn/operations/login';
import { logout } from '../fn/operations/logout';
import { Logout$Params } from '../fn/operations/logout';
import { logoutCallback } from '../fn/operations/logout-callback';
import { LogoutCallback$Params } from '../fn/operations/logout-callback';
import { NameType } from '../models/name-type';

@Injectable({ providedIn: 'root' })
export class ApiService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `login()` */
  static readonly LoginPath = '/auth/login';

  /**
   * starts oidc flow.
   *
   * starts the oidc flow and redirects to the acutal keycloak login page.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `login()` instead.
   *
   * This method doesn't expect any request body.
   */
  login$Response(params: Login$Params, context?: HttpContext): Observable<StrictHttpResponse<{
'errorMessage': NameType;
}>> {
    return login(this.http, this.rootUrl, params, context);
  }

  /**
   * starts oidc flow.
   *
   * starts the oidc flow and redirects to the acutal keycloak login page.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `login$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  login(params: Login$Params, context?: HttpContext): Observable<{
'errorMessage': NameType;
}> {
    return this.login$Response(params, context).pipe(
      map((r: StrictHttpResponse<{
'errorMessage': NameType;
}>): {
'errorMessage': NameType;
} => r.body)
    );
  }

  /** Path part for operation `callback()` */
  static readonly CallbackPath = '/auth/callback';

  /**
   * completes OIDC login.
   *
   * Handles the OIDC authorization code callback from Keycloak. Validates the login state,
   * exchanges the authorization code for tokens, creates a BFF session, sets session/CSRF cookies,
   * and redirects back to the frontend.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `callback()` instead.
   *
   * This method doesn't expect any request body.
   */
  callback$Response(params?: Callback$Params, context?: HttpContext): Observable<StrictHttpResponse<{
'errorMessage': NameType;
}>> {
    return callback(this.http, this.rootUrl, params, context);
  }

  /**
   * completes OIDC login.
   *
   * Handles the OIDC authorization code callback from Keycloak. Validates the login state,
   * exchanges the authorization code for tokens, creates a BFF session, sets session/CSRF cookies,
   * and redirects back to the frontend.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `callback$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  callback(params?: Callback$Params, context?: HttpContext): Observable<{
'errorMessage': NameType;
}> {
    return this.callback$Response(params, context).pipe(
      map((r: StrictHttpResponse<{
'errorMessage': NameType;
}>): {
'errorMessage': NameType;
} => r.body)
    );
  }

  /** Path part for operation `logout()` */
  static readonly LogoutPath = '/auth/logout';

  /**
   * Logout via Keycloak redirect (RP-Initiated Logout).
   *
   * Initiates a redirect-based logout flow. The browser is redirected to Keycloak's
   * end_session_endpoint with an id_token_hint, so Keycloak can propagate the logout
   * to external Identity Providers (SAML SLO, OIDC logout).
   * After Keycloak completes the full logout chain, the browser is redirected back
   * to /auth/logout/callback, which then redirects to the frontend callback_url.
   * This is the only way to ensure external IdP sessions are properly terminated.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `logout()` instead.
   *
   * This method doesn't expect any request body.
   */
  logout$Response(params: Logout$Params, context?: HttpContext): Observable<StrictHttpResponse<{
'errorMessage': NameType;
}>> {
    return logout(this.http, this.rootUrl, params, context);
  }

  /**
   * Logout via Keycloak redirect (RP-Initiated Logout).
   *
   * Initiates a redirect-based logout flow. The browser is redirected to Keycloak's
   * end_session_endpoint with an id_token_hint, so Keycloak can propagate the logout
   * to external Identity Providers (SAML SLO, OIDC logout).
   * After Keycloak completes the full logout chain, the browser is redirected back
   * to /auth/logout/callback, which then redirects to the frontend callback_url.
   * This is the only way to ensure external IdP sessions are properly terminated.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `logout$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  logout(params: Logout$Params, context?: HttpContext): Observable<{
'errorMessage': NameType;
}> {
    return this.logout$Response(params, context).pipe(
      map((r: StrictHttpResponse<{
'errorMessage': NameType;
}>): {
'errorMessage': NameType;
} => r.body)
    );
  }

  /** Path part for operation `logoutCallback()` */
  static readonly LogoutCallbackPath = '/auth/logout/callback';

  /**
   * Logout callback from Keycloak.
   *
   * Called by Keycloak after the full logout chain (including external IdP SLO) is complete.
   * Looks up the stored logout state via the state parameter and redirects the browser
   * back to the original frontend callback_url.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `logoutCallback()` instead.
   *
   * This method doesn't expect any request body.
   */
  logoutCallback$Response(params?: LogoutCallback$Params, context?: HttpContext): Observable<StrictHttpResponse<{
'errorMessage': NameType;
}>> {
    return logoutCallback(this.http, this.rootUrl, params, context);
  }

  /**
   * Logout callback from Keycloak.
   *
   * Called by Keycloak after the full logout chain (including external IdP SLO) is complete.
   * Looks up the stored logout state via the state parameter and redirects the browser
   * back to the original frontend callback_url.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `logoutCallback$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  logoutCallback(params?: LogoutCallback$Params, context?: HttpContext): Observable<{
'errorMessage': NameType;
}> {
    return this.logoutCallback$Response(params, context).pipe(
      map((r: StrictHttpResponse<{
'errorMessage': NameType;
}>): {
'errorMessage': NameType;
} => r.body)
    );
  }

}
